import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, MessageSquare, Shell, NotebookPen } from 'lucide-react';
import {
  Card,
  CardContent,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Separator,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@monksflow/monks-ui';
import { PersonaAvatar } from '@/shared/ui';
import { SectionHeader } from '@/shared/ui/SectionHeader';
import { getBrandContext } from '../../lib/brandContext';
import ReactMarkdown from 'react-markdown';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { personasByIdsQuery } from '@/entities/persona';
import type { FocusGroupResponseDto } from '@inference.engine/persona-api-client';
import { useTranslation } from 'react-i18next';

/** Which section the export renders — one PDF per section (see the report header). */
export type FocusGroupExportSection = 'summary' | 'interviews' | 'all';

interface FocusGroupReportContentProps {
  focusGroup: FocusGroupResponseDto;
  /** PDF export: drop the tab chrome and render the requested section expanded. */
  exporting?: boolean;
  /** Export only: which section to render (default `all`). */
  section?: FocusGroupExportSection;
}

/**
 * The completed-report body: an "Executive summary" + "Interviews" tab pair
 * rendered from a `FocusGroupResponseDto`. Shared between the full-page
 * `FocusGroupReport` and the in-project artifact preview modal so both show an
 * identical view. The component fills and scrolls within its parent, so the
 * host must constrain height (e.g. a `flex` column with `min-h-0`).
 */
export function FocusGroupReportContent({
  focusGroup,
  exporting = false,
  section = 'all',
}: FocusGroupReportContentProps) {
  const { t } = useTranslation('audiences');

  const workspaceId = useCurrentWorkspaceId();

  const personaIds = useMemo(
    () => (focusGroup.personaSummaries ?? []).map((ps) => ps.personaId),
    [focusGroup.personaSummaries],
  );

  const { data: personas } = useQuery(personasByIdsQuery(workspaceId, personaIds));

  const personaPhotoMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of personas ?? []) {
      if (p.photoAssetId) map.set(p.id, p.photoAssetId);
    }
    return map;
  }, [personas]);

  if (exporting) {
    return (
      <div className="flex flex-col font-default">
        {section !== 'interviews' && (
          <ExecutiveSummaryTab focusGroup={focusGroup} personaPhotoMap={personaPhotoMap} />
        )}
        {section !== 'summary' && <InterviewsTab focusGroup={focusGroup} exporting />}
      </div>
    );
  }

  return (
    <Tabs
      defaultValue="executive-summary"
      className="flex flex-col flex-1 min-h-0 overflow-hidden font-default"
    >
      <div className="flex h-[57px] shrink-0 items-center justify-center border-b border-black/5 dark:border-border-light">
        <TabsList>
          <TabsTrigger value="executive-summary" className="gap-2">
            <NotebookPen className="size-4" />
            {t('focusGroup.focusGroupReportContent.executiveSummary')}
          </TabsTrigger>
          {/* eslint-disable-next-line i18next/no-literal-string -- tab option value attribute */}
          <TabsTrigger value="interviews" className="gap-2">
            <MessageSquare size={16} />
            {t('focusGroup.focusGroupReportContent.interviews')}
          </TabsTrigger>
        </TabsList>
      </div>
      <TabsContent value="executive-summary" className="flex-1 overflow-y-auto mt-0">
        <ExecutiveSummaryTab focusGroup={focusGroup} personaPhotoMap={personaPhotoMap} />
      </TabsContent>
      {/* eslint-disable-next-line i18next/no-literal-string -- tab option value attribute */}
      <TabsContent value="interviews" className="flex-1 overflow-y-auto mt-0">
        <InterviewsTab focusGroup={focusGroup} />
      </TabsContent>
    </Tabs>
  );
}

function ExecutiveSummaryTab({
  focusGroup,
  personaPhotoMap,
}: {
  focusGroup: FocusGroupResponseDto;
  personaPhotoMap: Map<string, string>;
}) {
  const { t } = useTranslation('audiences');

  const summary = focusGroup.executiveSummary;
  const keyInsights = summary?.keyInsights ?? [];
  const commonThemes = summary?.commonThemes ?? [];
  const divergentViews = (summary?.divergentViews ?? [])
    .map((v) => (typeof v === 'string' ? { stat: '', description: v } : v))
    .filter((v) => v.description);
  const recommendations = summary?.recommendations ?? [];
  const personaSummaries = focusGroup.personaSummaries ?? [];
  const notableQuotes = summary?.notableQuotes ?? [];

  const brandContext = getBrandContext(focusGroup);

  return (
    <div className="@container flex flex-col gap-12 p-6">
      <div className="flex flex-col gap-2">
        <SectionHeader title={t('focusGroup.focusGroupReportContent.insight')} />
        {brandContext && (
          <p className="text-sm text-muted-foreground">
            {brandContext.applied
              ? brandContext.brandName
                ? t('focusGroup.focusGroupReportContent.runWithBrandContext', {
                    brandName: brandContext.brandName,
                  })
                : t('focusGroup.focusGroupReportContent.runWithBrandContextFromAnalysis')
              : t('focusGroup.focusGroupReportContent.brandContextUnavailable')}
          </p>
        )}
      </div>

      <div className="flex flex-wrap gap-4">
        <div className="flex-1 min-w-[300px] rounded-2xl bg-violet-50 dark:bg-badge-purple/50 px-8 py-10 flex flex-col gap-4">
          <span className="text-sm font-medium text-violet-600 dark:text-badge-purple-foreground">
            {t('focusGroup.focusGroupReportContent.researchTopic')}
          </span>
          <h2 className="text-heading-xl font-medium text-foreground">
            {focusGroup.userQuestions}
          </h2>
        </div>
        <div className="flex-1 min-w-[300px] rounded-2xl border border-border px-8 py-10 flex flex-col gap-4">
          <h3 className="text-heading-lg font-medium text-foreground">
            {t('focusGroup.focusGroupReportContent.executiveSummary2')}
          </h3>
          {summary?.overview ? (
            <div className="text-body-lg text-gray-600 dark:text-foreground-alt prose prose-sm max-w-none">
              <ReactMarkdown>{summary.overview}</ReactMarkdown>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              {t('focusGroup.focusGroupReportContent.noExecutiveSummaryAvailableYet')}
            </p>
          )}
        </div>
      </div>

      {keyInsights.length > 0 && (
        <section className="flex flex-col gap-6">
          <SectionHeader title={t('focusGroup.focusGroupReportContent.topInsights')} />
          <div className="grid grid-cols-1 @lg:grid-cols-2 @3xl:grid-cols-3 gap-4">
            {keyInsights.map((insight, index) => (
              <Card
                key={`${index.toString()}-${insight.slice(0, 24)}`}
                variant="outline"
                className="shadow-none border-gray-200 dark:border-border-light"
              >
                <CardContent className="px-6 flex flex-col gap-3">
                  <span className="shrink-0 size-9 rounded-lg bg-violet-100 text-violet-600 dark:bg-badge-purple dark:text-badge-purple-foreground text-heading-md font-medium flex items-center justify-center">
                    {index + 1}
                  </span>
                  <p className="text-heading-md font-normal leading-7">{insight}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      )}

      {commonThemes.length > 0 && (
        <section className="flex flex-col gap-6">
          <SectionHeader title={t('focusGroup.focusGroupReportContent.commonThemes')} />
          <div className="grid grid-cols-1 @lg:grid-cols-2 @3xl:grid-cols-3 gap-4">
            {commonThemes.map((theme, index) => (
              <Card
                key={`${index.toString()}-${theme.slice(0, 24)}`}
                className="shadow-none border-none bg-gray-50 dark:bg-surface-raised"
              >
                <CardContent className="px-6">
                  <p className="text-body-lg leading-relaxed">{theme}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      )}

      {divergentViews.length > 0 && (
        <section className="flex flex-col gap-6">
          <SectionHeader title={t('focusGroup.focusGroupReportContent.divergentViews')} />
          <div className="grid grid-cols-1 @lg:grid-cols-2 gap-6">
            {divergentViews.map((view, index) => (
              <div
                key={`${index.toString()}-${view.description.slice(0, 24)}`}
                className="flex flex-col gap-2 bg-card border border-black/10 dark:border-border-light rounded-lg p-6"
              >
                {view.stat && (
                  <p className="text-[50px] leading-[64px] font-normal text-foreground tracking-tight">
                    {view.stat}
                  </p>
                )}
                <p className="text-body-lg text-gray-600 dark:text-foreground-alt">
                  {view.description}
                </p>
              </div>
            ))}
          </div>
        </section>
      )}

      {recommendations.length > 0 && (
        <section className="flex flex-col gap-6">
          <SectionHeader title={t('focusGroup.focusGroupReportContent.recommendations')} />
          <div className="grid grid-cols-1 @lg:grid-cols-2 @3xl:grid-cols-3 gap-6">
            {recommendations.map((rec, index) => (
              <div
                key={`${index.toString()}-${rec.action.slice(0, 24)}`}
                className="flex flex-col gap-6 border border-black/10 dark:border-border-light rounded-lg p-6"
              >
                <div className="flex items-center gap-2">
                  <span className="shrink-0 size-9 rounded bg-accent text-heading-md font-medium text-primary flex items-center justify-center">
                    {index + 1}
                  </span>
                  <p className="text-heading-md font-medium text-foreground tracking-tight">
                    {rec.category}
                  </p>
                </div>
                <div className="flex flex-col gap-4">
                  <p className="text-body-lg text-foreground tracking-tight">{rec.action}</p>
                  <p className="text-body-md leading-6 text-gray-600 dark:text-foreground-alt tracking-tight">
                    {rec.rationale}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {notableQuotes.length > 0 && (
        <section className="flex flex-col gap-6">
          <SectionHeader title={t('focusGroup.focusGroupReportContent.notableQuotes')} />
          <div className="grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(240px,1fr))] [grid-template-rows:auto_auto_1fr]">
            {notableQuotes.map((quote, index) => (
              <div
                key={`${index.toString()}-${quote.speaker}`}
                className="grid [grid-template-rows:subgrid] [grid-row:span_3] gap-6 bg-card border border-black/10 dark:border-border-light rounded-lg p-6"
              >
                <div className="flex gap-4 items-start bg-accent rounded-lg p-3 overflow-hidden">
                  <span className="text-[57px] leading-[64px] text-primary tracking-tight shrink-0">
                    &ldquo;
                  </span>
                  <p className="text-body-md font-medium text-primary leading-5">
                    {quote.quote}&rdquo;
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="shrink-0 size-12 rounded-full overflow-hidden">
                    <PersonaAvatar
                      name={quote.speaker}
                      photoAssetId={personaPhotoMap.get(quote.speakerId)}
                      size={48}
                    />
                  </div>
                  <div className="flex flex-col gap-0.5">
                    <p className="text-body-lg font-medium text-foreground">{quote.speaker}</p>
                  </div>
                </div>
                {quote.context && (
                  <p className="text-body-md text-gray-600 dark:text-foreground-alt leading-5">
                    {quote.context}
                  </p>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {personaSummaries.length > 0 && (
        <section className="flex flex-col gap-6">
          <SectionHeader title={t('focusGroup.focusGroupReportContent.individualInsights')} />
          <div className="grid grid-cols-1 @lg:grid-cols-2 gap-4">
            {personaSummaries.map((ps) => (
              <div
                key={ps.personaId}
                className="flex flex-col gap-6 bg-card border border-black/10 dark:border-border-light rounded-lg p-6"
              >
                <h3 className="text-heading-lg font-medium text-foreground">{ps.personaName}</h3>
                <div className="flex gap-6 items-start">
                  <div className="shrink-0 w-[152px] h-[216px] rounded-lg overflow-hidden">
                    <PersonaAvatar
                      name={ps.personaName}
                      photoAssetId={personaPhotoMap.get(ps.personaId)}
                      size={152}
                    />
                  </div>
                  <p className="text-body-md text-foreground leading-6 flex-1">{ps.summary}</p>
                </div>
                {ps.keyInsights.length > 0 && (
                  <div className="flex flex-col gap-4">
                    <p className="text-body-lg font-medium text-foreground">
                      {t('focusGroup.focusGroupReportContent.keyInsights')}
                    </p>
                    <div className="flex flex-col gap-4">
                      {ps.keyInsights.map((insight, i) => (
                        <div
                          key={i}
                          className="flex items-start gap-6 bg-gray-50 dark:bg-surface-raised rounded-lg p-6"
                        >
                          <span className="shrink-0 mt-2 size-2 rounded-full bg-foreground" />
                          <p className="text-body-md text-foreground leading-6">{insight}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

type Conversation = NonNullable<FocusGroupResponseDto['conversations']>[number];

function InterviewsTab({
  focusGroup,
  exporting = false,
}: {
  focusGroup: FocusGroupResponseDto;
  exporting?: boolean;
}) {
  const { t } = useTranslation('audiences');

  const conversations = focusGroup.conversations ?? [];

  if (conversations.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">
          {t('focusGroup.focusGroupReportContent.noInterviewDataAvailable')}
        </p>
      </div>
    );
  }

  const transformedQuestions = focusGroup.transformedQuestions ?? [];

  return (
    <div className="flex flex-col gap-12 px-6 py-6">
      <section className="flex flex-col gap-6">
        <div className="flex gap-4 items-center">
          <div className="flex items-center justify-center size-[54px] rounded bg-gray-50 dark:bg-surface-raised shrink-0">
            <Shell className="size-6 text-muted-foreground" />
          </div>
          <div className="flex flex-col">
            <h2 className="text-2xl font-medium text-foreground tracking-tight">
              {t('focusGroup.focusGroupReportContent.transcriptsPerPersona')}
            </h2>
            <p className="text-sm text-muted-foreground">
              {t('focusGroup.focusGroupReportContent.completeConversationHistoryWithAll')}
            </p>
          </div>
        </div>
        <div className="flex flex-col gap-4">
          {conversations.map((conv, index) => (
            <PersonaTranscriptCard
              key={conv.personaId}
              conversation={conv}
              defaultExpanded={index === 0}
              exporting={exporting}
            />
          ))}
        </div>
      </section>

      {transformedQuestions.length > 0 && (
        <section className="flex flex-col gap-6">
          <div className="flex gap-4 items-center">
            <div className="flex items-center justify-center size-[54px] rounded bg-gray-50 dark:bg-surface-raised shrink-0">
              <Shell className="size-6 text-muted-foreground" />
            </div>
            <div className="flex flex-col">
              <h2 className="text-2xl font-medium text-foreground tracking-tight">
                {t('focusGroup.focusGroupReportContent.transcriptsPerQuestion')}
              </h2>
              <p className="text-sm text-muted-foreground">
                {t('focusGroup.focusGroupReportContent.completeConversationHistoryWithAll')}
              </p>
            </div>
          </div>
          <div className="flex flex-col gap-4">
            {transformedQuestions.map((question, index) => (
              <QuestionTranscriptCard
                key={`q-${index.toString()}`}
                question={question}
                conversations={conversations}
                questionIndex={index}
                exporting={exporting}
              />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function PersonaTranscriptCard({
  conversation,
  defaultExpanded = false,
  exporting = false,
}: {
  conversation: Conversation;
  defaultExpanded?: boolean;
  exporting?: boolean;
}) {
  const { t } = useTranslation('audiences');

  const { personaName } = conversation;

  const header = (
    <>
      <div className="flex items-center gap-4">
        <PersonaAvatar name={personaName} size={54} />
        <div className="flex flex-col items-start">
          <span className="text-2xl font-medium text-foreground tracking-tight">{personaName}</span>
        </div>
      </div>
      {!exporting && (
        <ChevronDown
          size={20}
          className="text-muted-foreground shrink-0 transition-transform group-data-[state=open]:rotate-180"
        />
      )}
    </>
  );

  const body = (
    <div className="flex flex-col items-center gap-8 pb-10">
      <Separator />
      {conversation.conversation.map((entry, idx) => (
        <div key={idx} className="flex flex-col gap-8">
          <div className="flex flex-col gap-4 items-start pr-20">
            <span className="text-sm font-medium text-foreground">
              {t('focusGroup.focusGroupReportContent.moderator')}
            </span>
            <div className="bg-gray-50 dark:bg-surface-raised px-5 py-4 rounded-b-2xl rounded-tr-2xl max-w-[450px]">
              <p className="text-base text-foreground leading-relaxed">{entry.question}</p>
            </div>
          </div>
          <div className="flex flex-col gap-4 items-start pl-20">
            <div className="flex items-center gap-2">
              <PersonaAvatar name={personaName} size={24} />
              <span className="text-sm font-medium text-foreground">{personaName}</span>
            </div>
            <div className="bg-sky-100 dark:bg-badge-blue px-5 py-4 rounded-b-2xl rounded-tl-2xl max-w-[450px]">
              <p className="text-base text-sky-900 dark:text-badge-blue-foreground leading-relaxed">
                {entry.answer}
              </p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  // Export: static (no Collapsible) — no open-animation to race the height measure.
  if (exporting) {
    return (
      <div className="border border-black/10 dark:border-border-light rounded-lg p-6 flex flex-col gap-6">
        <div className="flex items-center justify-between w-full text-left">{header}</div>
        {body}
      </div>
    );
  }

  return (
    <Collapsible defaultOpen={defaultExpanded}>
      <div className="border border-black/10 dark:border-border-light rounded-lg p-6 flex flex-col gap-6">
        <CollapsibleTrigger className="flex items-center justify-between w-full text-left group">
          {header}
        </CollapsibleTrigger>
        <CollapsibleContent>{body}</CollapsibleContent>
      </div>
    </Collapsible>
  );
}

function QuestionTranscriptCard({
  question,
  conversations,
  questionIndex,
  defaultExpanded = false,
  exporting = false,
}: {
  question: string;
  conversations: Conversation[];
  questionIndex: number;
  defaultExpanded?: boolean;
  exporting?: boolean;
}) {
  const header = (
    <>
      <div className="flex items-center gap-4 flex-1 min-w-0">
        <div className="flex items-center justify-center size-9 rounded bg-gray-50 dark:bg-surface-raised shrink-0 text-sm font-medium text-muted-foreground">
          {questionIndex + 1}
        </div>
        <p className="text-base font-medium text-foreground">{question}</p>
      </div>
      {!exporting && (
        <ChevronDown
          size={24}
          className="text-muted-foreground shrink-0 transition-transform group-data-[state=open]:rotate-180"
        />
      )}
    </>
  );

  const body = (
    <div className="flex flex-col items-center gap-8">
      <Separator />
      {conversations.map((conv) => (
        <div key={conv.personaId} className="flex flex-col gap-4 items-start pl-20">
          <div className="flex items-center gap-2">
            <PersonaAvatar name={conv.personaName} size={24} />
            <span className="text-sm font-medium text-foreground">{conv.personaName}</span>
          </div>
          <div className="bg-sky-100 dark:bg-badge-blue px-5 py-4 rounded-b-2xl rounded-tl-2xl max-w-[450px]">
            <p className="text-base text-sky-900 dark:text-badge-blue-foreground leading-relaxed">
              {conv.conversation[questionIndex]?.answer}
            </p>
          </div>
        </div>
      ))}
    </div>
  );

  // Export: static (no Collapsible) — no open-animation to race the height measure.
  if (exporting) {
    return (
      <div className="border border-black/10 dark:border-border-light rounded-lg p-6 flex flex-col gap-6">
        <div className="flex items-center gap-4 w-full text-left">{header}</div>
        {body}
      </div>
    );
  }

  return (
    <Collapsible defaultOpen={defaultExpanded}>
      <div className="border border-black/10 dark:border-border-light rounded-lg p-6 flex flex-col gap-6">
        <CollapsibleTrigger className="flex items-center gap-4 w-full text-left group">
          {header}
        </CollapsibleTrigger>
        <CollapsibleContent>{body}</CollapsibleContent>
      </div>
    </Collapsible>
  );
}
